import classNames from 'classnames'
import PropTypes from 'prop-types'
import React from 'react'

import styles from './styles.module.scss'
import HelperText from '../HelperText'

const FormGroup = React.forwardRef(
  (
    {
      className,
      children,
      disabled,
      labelSrOnly,
      label,
      labelDescription,
      helperText,
      style,
      ...props
    },
    ref
  ) => {
    // const { controlId } = useContext(FormContext)
    const renderChildren = () =>
      // Remove FormRow margin
      React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type.name === 'FormRow') {
          return React.cloneElement(child, {
            style: child.props.style,
            className: classNames(child.props.className, styles.isChildFormRow),
          })
        }
        return child
      })

    return (
      <fieldset disabled={disabled} ref={ref} {...props}>
        {/* Wrapper element necessary as you can't use flexbox on fieldsets */}
        <div style={style} className={classNames(styles.formGroup, className)}>
          <legend className={classNames({ 'u-visually-hidden': labelSrOnly })}>
            {label}
            {labelDescription && (
              <HelperText className={styles.labelDescription}>
                {labelDescription}
              </HelperText>
            )}
          </legend>
          <div>{renderChildren()}</div>
          {helperText && <HelperText>{helperText}</HelperText>}
        </div>
      </fieldset>
    )
  }
)

FormGroup.displayName = 'FormGroup'

FormGroup.propTypes = {
  /**
   * The FormGroup `ref` will be forwarded to the underlying element.
   * Unless the FormGroup is rendered `as` a composite component,
   * it will be a DOM node, when resolved.
   *
   * @type {ReactRef}
   * @alias ref
   */
  _ref: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  /**
   * Used to label the form group. Under the hood, this is
   * is the legend for the fieldset. If you don't want this
   * to visually display, use the labelSrOnly prop
   */
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,

  /**
   * Hides the label visually while still allowing it to be
   * read by assistive technologies.
   */
  labelSrOnly: PropTypes.bool,

  /**
   * Disabled all form inputs within the FormGroup
   */
  disabled: PropTypes.bool,
}

FormGroup.defaultProps = {
  _ref: null,
  labelSrOnly: false,
  disabled: false,
}

export default FormGroup
